import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export interface HistoryItem {
  id: string;
  imageUrl: string;
  prompt: string;
  style: string;
  aspectRatio: string;
  createdAt: number;
}

interface ImageHistoryProps {
  onUsePrompt: (prompt: string) => void;
  refreshKey: number;
}

const STORAGE_KEY = 'ai_image_history';

export function getHistory(): HistoryItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as HistoryItem[];
  } catch {
    return [];
  }
}

export function saveToHistory(item: HistoryItem) {
  const history = getHistory();
  history.unshift(item);
  // Keep max 50 items
  if (history.length > 50) history.length = 50;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
}

export function removeFromHistory(id: string) {
  const history = getHistory().filter((h) => h.id !== id);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
}

export function clearAllHistory() {
  localStorage.removeItem(STORAGE_KEY);
}

export default function ImageHistory({ onUsePrompt, refreshKey }: ImageHistoryProps) {
  const { t } = useTranslation();
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [selectedImage, setSelectedImage] = useState<HistoryItem | null>(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    setHistory(getHistory());
  }, [refreshKey]);

  const handleDelete = (id: string) => {
    removeFromHistory(id);
    setHistory(getHistory());
    if (selectedImage?.id === id) setSelectedImage(null);
  };

  const handleClearAll = () => {
    clearAllHistory();
    setHistory([]);
    setShowClearConfirm(false);
    setSelectedImage(null);
  };

  const handleCopyPrompt = (item: HistoryItem) => {
    navigator.clipboard.writeText(item.prompt).then(() => {
      setCopiedId(item.id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const handleDownload = (item: HistoryItem) => {
    const link = document.createElement('a');
    link.href = item.imageUrl;
    link.download = `ai-generated-${item.id}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatDate = (ts: number) => {
    const d = new Date(ts);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return t('history.justNow');
    if (diffMin < 60) return t('history.minutesAgo', { count: diffMin });
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return t('history.hoursAgo', { count: diffHr });
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 7) return t('history.daysAgo', { count: diffDay });
    return d.toLocaleDateString();
  };

  if (history.length === 0) {
    return (
      <div className="bg-[#252830] rounded-2xl p-8 shadow-lg border-2 border-gray-800">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary/20">
            <i className="ri-history-line text-primary text-lg"></i>
          </div>
          <h3 className="font-black text-white text-base">{t('history.title')}</h3>
        </div>
        <div className="text-center py-12">
          <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-[#1e2025] rounded-2xl">
            <i className="ri-image-line text-3xl text-gray-600"></i>
          </div>
          <p className="text-gray-500 text-sm font-semibold">{t('history.empty')}</p>
          <p className="text-gray-600 text-xs mt-1 font-medium">{t('history.emptyHint')}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-[#252830] rounded-2xl p-8 shadow-lg border-2 border-gray-800">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary/20">
              <i className="ri-history-line text-primary text-lg"></i>
            </div>
            <h3 className="font-black text-white text-base">{t('history.title')}</h3>
            <span className="px-2.5 py-1 bg-[#1e2025] rounded-full text-xs font-bold text-gray-400 border border-gray-700">
              {history.length}
            </span>
          </div>
          <button
            onClick={() => setShowClearConfirm(true)}
            className="text-xs text-gray-500 hover:text-red-400 font-semibold flex items-center gap-1 cursor-pointer transition-colors whitespace-nowrap"
          >
            <i className="ri-delete-bin-line"></i>
            {t('history.clearAll')}
          </button>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {history.map((item) => (
            <div
              key={item.id}
              className="group relative rounded-xl overflow-hidden border border-gray-700 hover:border-primary/40 hover:shadow-lg transition-all cursor-pointer"
              onClick={() => setSelectedImage(item)}
            >
              <div className="aspect-square w-full">
                <img
                  src={item.imageUrl}
                  alt={item.prompt}
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                />
              </div>
              {/* Hover overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex flex-col justify-end p-2.5">
                <p className="text-white text-[10px] leading-tight line-clamp-2 font-medium mb-1.5">
                  {item.prompt}
                </p>
                <div className="flex items-center gap-1">
                  <button
                    onClick={(e) => { e.stopPropagation(); handleCopyPrompt(item); }}
                    className={`px-2 py-1 rounded-md text-[10px] font-bold transition-all whitespace-nowrap cursor-pointer ${
                      copiedId === item.id
                        ? 'bg-green-500 text-white'
                        : 'bg-white/20 backdrop-blur-sm text-white hover:bg-white/30'
                    }`}
                  >
                    <i className={`${copiedId === item.id ? 'ri-check-line' : 'ri-clipboard-line'} mr-0.5`}></i>
                    {copiedId === item.id ? t('gallery.copied') : t('gallery.copyPrompt')}
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); onUsePrompt(item.prompt); }}
                    className="px-2 py-1 bg-primary rounded-md text-[10px] font-bold text-white hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-magic-line mr-0.5"></i>
                    {t('gallery.usePrompt')}
                  </button>
                </div>
              </div>
              {/* Time badge */}
              <div className="absolute top-2 right-2 px-1.5 py-0.5 bg-black/40 backdrop-blur-sm rounded text-[9px] text-white font-medium opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                {formatDate(item.createdAt)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <div
            className="bg-[#252830] rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl animate-fadeIn border border-gray-700"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Image */}
            <div className="relative bg-[#1e2025]">
              <img
                src={selectedImage.imageUrl}
                alt={selectedImage.prompt}
                className="w-full max-h-[60vh] object-contain"
              />
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-black/40 backdrop-blur-sm rounded-full text-white hover:bg-black/60 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            {/* Details */}
            <div className="p-6">
              <div className="flex items-start gap-3 mb-4">
                <div className="flex-1">
                  <p className="text-sm text-gray-300 font-semibold leading-relaxed">{selectedImage.prompt}</p>
                  <div className="flex items-center gap-3 mt-2">
                    <span className="px-2 py-0.5 bg-[#1e2025] rounded text-xs font-bold text-gray-400 border border-gray-700">
                      {selectedImage.style === 'none' ? 'Default' : selectedImage.style}
                    </span>
                    <span className="px-2 py-0.5 bg-[#1e2025] rounded text-xs font-bold text-gray-400 border border-gray-700">
                      {selectedImage.aspectRatio}
                    </span>
                    <span className="text-xs text-gray-500 font-medium">
                      {formatDate(selectedImage.createdAt)}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => { onUsePrompt(selectedImage.prompt); setSelectedImage(null); }}
                  className="flex-1 px-4 py-3 bg-primary text-white rounded-xl font-bold text-sm hover:bg-primary-600 transition-all flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-magic-line"></i>
                  {t('history.reuse')}
                </button>
                <button
                  onClick={() => handleDownload(selectedImage)}
                  className="px-4 py-3 bg-[#1e2025] text-gray-300 rounded-xl font-bold text-sm hover:bg-[#2d3139] transition-all flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap border border-gray-700"
                >
                  <i className="ri-download-line"></i>
                  {t('history.download')}
                </button>
                <button
                  onClick={() => { handleCopyPrompt(selectedImage); }}
                  className={`px-4 py-3 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap border ${
                    copiedId === selectedImage.id
                      ? 'bg-green-500/20 text-green-400 border-green-500/30'
                      : 'bg-[#1e2025] text-gray-300 hover:bg-[#2d3139] border-gray-700'
                  }`}
                >
                  <i className={copiedId === selectedImage.id ? 'ri-check-line' : 'ri-clipboard-line'}></i>
                  {copiedId === selectedImage.id ? t('gallery.copied') : t('history.copyPrompt')}
                </button>
                <button
                  onClick={() => { handleDelete(selectedImage.id); }}
                  className="px-4 py-3 bg-red-500/10 text-red-400 rounded-xl font-bold text-sm hover:bg-red-500/20 transition-all flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap border border-red-500/30"
                >
                  <i className="ri-delete-bin-line"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Clear Confirm Modal */}
      {showClearConfirm && (
        <div
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setShowClearConfirm(false)}
        >
          <div
            className="bg-[#252830] rounded-2xl p-8 max-w-sm w-full shadow-2xl animate-fadeIn text-center border border-gray-700"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 bg-red-500/10 rounded-2xl border border-red-500/30">
              <i className="ri-delete-bin-line text-2xl text-red-400"></i>
            </div>
            <h3 className="text-lg font-black text-white mb-2">{t('history.clearConfirmTitle')}</h3>
            <p className="text-sm text-gray-400 font-medium mb-6">{t('history.clearConfirmDesc')}</p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowClearConfirm(false)}
                className="flex-1 px-4 py-3 bg-[#1e2025] text-gray-300 rounded-xl font-bold text-sm hover:bg-[#2d3139] transition-all cursor-pointer whitespace-nowrap border border-gray-700"
              >
                {t('history.cancel')}
              </button>
              <button
                onClick={handleClearAll}
                className="flex-1 px-4 py-3 bg-red-500 text-white rounded-xl font-bold text-sm hover:bg-red-600 transition-all cursor-pointer whitespace-nowrap"
              >
                {t('history.clearConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
